import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../types/chemistry';
import { chemistryApi } from '../../api/chemistry';
import ProjectCard from '../../components/projects/ProjectCard';
import PageHeader from '../../components/common/PageHeader';
import PageTransition from '../../components/common/PageTransition';
import UpgradePrompt, { GuestGate } from '../../components/common/UpgradePrompt';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../stores/uiStore';
import { formatRelativeDate } from '../../utils/dateFormat';
import '../../components/projects/ProjectCard.css';

type ViewMode = 'cards' | 'list';
type SortBy = 'name' | 'date' | 'molecules';
type Tab = 'active' | 'archived';

function formatCount(n: number): string {
  if (n === 0) return '\u2014';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  const isMember = !isGuest;
  const showNotification = useUIStore((s) => s.showNotification);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('active');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    project: Project;
    confirmText: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem('discoverant_projects_view') as ViewMode) || 'cards'
  );
  const [sortBy, setSortBy] = useState<SortBy>('date');

  // Create project modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#10B981');
  const [isCreating, setIsCreating] = useState(false);

  const colorOptions = [
    '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B',
    '#EF4444', '#EC4899', '#14B8A6', '#6366F1',
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    localStorage.setItem('discoverant_projects_view', viewMode);
  }, [viewMode]);

  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [active, archived] = await Promise.all([
        chemistryApi.getProjects(),
        chemistryApi.getArchivedProjects(),
      ]);
      setProjects(active);
      setArchivedProjects(archived);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  };

  const sortedProjects = useMemo(() => {
    const list = activeTab === 'active' ? projects : archivedProjects;
    const sorted = [...list];
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'molecules':
        sorted.sort((a, b) => (b.moleculeCount || 0) - (a.moleculeCount || 0));
        break;
      case 'date':
      default:
        sorted.sort((a, b) => {
          const aDate = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const bDate = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          return bDate - aDate;
        });
        break;
    }
    return sorted;
  }, [projects, archivedProjects, activeTab, sortBy]);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setIsCreating(true);
    try {
      await chemistryApi.createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
        color: newProjectColor,
      });
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectColor('#10B981');
      setIsCreateModalOpen(false);
      await fetchProjects();
    } catch (err: any) {
      alert(err.message || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    navigate(`/chemistry/files?project=${project.id}`);
  };

  const handleRenameProject = async (id: number, newName: string) => {
    try {
      await chemistryApi.updateProject(id, { name: newName });
      await fetchProjects();
    } catch (err: any) {
      alert(err.message || 'Failed to rename project');
    }
  };

  const handleArchiveProject = async (id: number) => {
    try {
      await chemistryApi.archiveProject(id);
      showNotification('success', 'Project archived');
      await fetchProjects();
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to archive project');
    }
  };

  const handleRestoreProject = async (id: number) => {
    try {
      await chemistryApi.restoreProject(id);
      showNotification('success', 'Project restored');
      await fetchProjects();
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to restore project');
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await chemistryApi.deleteProject(deleteConfirm.project.id);
      showNotification('success', `"${deleteConfirm.project.name}" permanently deleted`);
      setDeleteConfirm(null);
      await fetchProjects();
    } catch (err: any) {
      showNotification('error', err.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderToolbar = () => (
    <div className="flex items-center gap-3">
      {/* Sort dropdown */}
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value as SortBy)}
        className="px-3 py-1.5 text-xs font-medium text-white/80 bg-white/10 border border-white/20 rounded-lg appearance-none cursor-pointer hover:bg-white/20 transition-colors"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '24px' }}
      >
        <option value="date">Date Modified</option>
        <option value="name">Name</option>
        <option value="molecules">Molecule Count</option>
      </select>

      {/* View toggle */}
      <div className="flex rounded-lg border border-white/20 overflow-hidden">
        <button
          onClick={() => setViewMode('cards')}
          className={`px-2.5 py-1.5 transition-colors ${viewMode === 'cards' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}
          title="Card view"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`px-2.5 py-1.5 transition-colors ${viewMode === 'list' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}
          title="List view"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* New project button */}
      {activeTab === 'active' && (
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-white/15 border border-white/20 rounded-lg hover:bg-white/25 transition-colors flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Project
        </button>
      )}
    </div>
  );

  const renderTabs = () => (
    <div className="flex gap-1 mb-6">
      <button
        onClick={() => setActiveTab('active')}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          activeTab === 'active'
            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
      >
        Active
        {projects.length > 0 && (
          <span className="ml-1.5 text-xs opacity-60">({projects.length})</span>
        )}
      </button>
      <button
        onClick={() => setActiveTab('archived')}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          activeTab === 'archived'
            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
      >
        Archived
        {archivedProjects.length > 0 && (
          <span className="ml-1.5 text-xs opacity-60">({archivedProjects.length})</span>
        )}
      </button>
    </div>
  );

  const renderListView = () => (
    <div className="project-list">
      <table className="project-list__table">
        <thead>
          <tr>
            <th style={{ width: '3%' }}></th>
            <th style={{ width: '30%' }} onClick={() => setSortBy('name')}>
              Name
              {sortBy === 'name' && <span className="sort-arrow sort-arrow--active">&#9650;</span>}
            </th>
            <th style={{ width: '12%', textAlign: 'right' }} onClick={() => setSortBy('molecules')}>
              Molecules
              {sortBy === 'molecules' && <span className="sort-arrow sort-arrow--active">&#9660;</span>}
            </th>
            <th style={{ width: '10%', textAlign: 'right' }}>Files</th>
            <th style={{ width: '10%', textAlign: 'right' }}>Reactions</th>
            <th style={{ width: '15%', textAlign: 'right' }} onClick={() => setSortBy('date')}>
              Modified
              {sortBy === 'date' && <span className="sort-arrow sort-arrow--active">&#9660;</span>}
            </th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {sortedProjects.map((project) => {
            const counts = project.contentCounts || { molecules: 0, reactions: 0, proteins: 0, files: 0 };
            return (
              <tr
                key={project.id}
                onClick={() => activeTab === 'active' ? handleProjectClick(project) : undefined}
                style={activeTab === 'archived' ? { opacity: 0.6, cursor: 'default' } : undefined}
              >
                <td><span className="project-list__color-dot" style={{ backgroundColor: project.color }} /></td>
                <td>
                  <span className="project-list__name">{project.name}</span>
                  {project.name.startsWith('Sample:') && (
                    <span className="project-card__badge project-card__badge--demo" style={{ marginLeft: 6 }}>DEMO</span>
                  )}
                  {activeTab === 'archived' && (
                    <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#f59e0b20', color: '#d97706', fontWeight: 600 }}>ARCHIVED</span>
                  )}
                </td>
                <td className="project-list__count" style={{ textAlign: 'right' }}>{formatCount(counts.molecules)}</td>
                <td className="project-list__count" style={{ textAlign: 'right' }}>{counts.files || '\u2014'}</td>
                <td className="project-list__count" style={{ textAlign: 'right' }}>{formatCount(counts.reactions)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                  {formatRelativeDate(project.lastModified)}
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="project-list__desc">{project.description || ''}</span>
                    <div className="flex gap-1 ml-auto" onClick={(e) => e.stopPropagation()}>
                      {activeTab === 'archived' && (
                        <button
                          onClick={() => handleRestoreProject(project.id)}
                          className="px-2 py-0.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors"
                        >
                          Restore
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm({ project, confirmText: '' })}
                        className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderCardView = () => (
    <div className="project-grid">
      {/* Create New Project Card — only in active tab */}
      {activeTab === 'active' && (
        <GuestGate tooltip="Create your own projects. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="project-card project-card--create"
          >
            <div className="project-card__create-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="project-card__create-label">New Project</span>
            <span className="project-card__create-sublabel">Create a new project</span>
          </button>
        </GuestGate>
      )}

      {/* Project Cards */}
      {sortedProjects.map((project) => (
        <ProjectCard
          key={project.id}
          id={project.id}
          name={project.name}
          description={project.description}
          color={project.color}
          contentCounts={project.contentCounts}
          lastModified={project.lastModified}
          patentRiskCount={project.patentRiskCount}
          archived={activeTab === 'archived'}
          onClick={() => activeTab === 'active' ? handleProjectClick(project) : undefined}
          onRename={activeTab === 'active' ? handleRenameProject : undefined}
          onArchive={activeTab === 'active' ? handleArchiveProject : undefined}
          onDelete={activeTab === 'active' ? () => setDeleteConfirm({ project, confirmText: '' }) : undefined}
          onRestore={activeTab === 'archived' ? () => handleRestoreProject(project.id) : undefined}
          onPermanentDelete={activeTab === 'archived' ? () => setDeleteConfirm({ project, confirmText: '' }) : undefined}
        />
      ))}
    </div>
  );

  const renderEmptyArchived = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 mb-4 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
        <svg className="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      </div>
      <p className="text-gray-400 dark:text-gray-500 text-sm">No archived projects</p>
    </div>
  );

  return (
    <PageTransition>
    <div className="min-h-screen" style={{ background: 'var(--content-bg)' }}>
      <PageHeader
        title="Projects"
        subtitle={`${projects.length} project${projects.length !== 1 ? 's' : ''}${archivedProjects.length > 0 ? ` \u00b7 ${archivedProjects.length} archived` : ''}`}
        actions={!isLoading && (projects.length > 0 || archivedProjects.length > 0) ? renderToolbar() : undefined}
        breadcrumbs={[{ label: 'Chemistry', to: '/chemistry/files' }, { label: 'Projects' }]}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <span className="text-gray-500 dark:text-gray-400 font-medium">Loading projects...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-red-500 bg-red-50 px-6 py-4 rounded-xl font-medium">{error}</div>
          </div>
        ) : projects.length === 0 && archivedProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-20 h-20 mb-6 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center">
              <svg className="w-10 h-10 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Welcome to Adroit</h2>
            <p className="text-gray-400 text-center max-w-md mb-6">
              Create your first project to start organising your chemistry data.
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-xl hover:from-teal-600 hover:to-emerald-600 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Create Project
            </button>
          </div>
        ) : (
          <>
            {/* Tabs — only show if there are archived projects */}
            {archivedProjects.length > 0 && renderTabs()}

            {activeTab === 'archived' && archivedProjects.length === 0 ? (
              renderEmptyArchived()
            ) : viewMode === 'list' ? (
              renderListView()
            ) : (
              renderCardView()
            )}
          </>
        )}
      </div>

      {/* Create Project Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
              <h2 className="text-xl font-semibold text-white">Create New Project</h2>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Project Name</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Describe your project..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white dark:focus:bg-gray-800 transition-all resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewProjectColor(color)}
                      className={`w-10 h-10 rounded-xl transition-all ${
                        newProjectColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewProjectName('');
                  setNewProjectDescription('');
                  setNewProjectColor('#10B981');
                }}
                className="px-5 py-2.5 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || isCreating}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
              >
                {isCreating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white">Delete Project</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-red-700 dark:text-red-300">
                  <p className="font-semibold mb-1">This action cannot be undone.</p>
                  <p>
                    Delete <strong>"{deleteConfirm.project.name}"</strong>? This will permanently remove all compounds, files, reactions, and enrichment data in this project.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type <strong>{deleteConfirm.project.name}</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirm.confirmText}
                  onChange={(e) => setDeleteConfirm({ ...deleteConfirm, confirmText: e.target.value })}
                  placeholder={deleteConfirm.project.name}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && deleteConfirm.confirmText === deleteConfirm.project.name) {
                      handlePermanentDelete();
                    }
                    if (e.key === 'Escape') setDeleteConfirm(null);
                  }}
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2.5 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePermanentDelete}
                disabled={deleteConfirm.confirmText !== deleteConfirm.project.name || isDeleting}
                className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-all"
              >
                {isDeleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <UpgradePrompt isOpen={showUpgradePrompt} onClose={() => setShowUpgradePrompt(false)} />
    </PageTransition>
  );
};

export default ProjectsPage;
